import React from 'react';

import classNames from 'classnames';

import { domHooks } from '@nxte-nl/dom-hooks';

import { hydrationHooks } from '../../hooks/hydrationHooks/hydrationHooks';
import { selectHooks } from '../../hooks/selectHooks/selectHooks';
import {
  CustomErrorMessage,
  CustomErrorMessageColor,
} from '../CustomErrorMessage/CustomErrorMessage';
import { SelectHeaderValue } from '../SelectHeaderValue/SelectHeaderValue';
import { SelectItem } from '../SelectItem/SelectItem';
import { Typography } from '../Typography/Typography';
import { Chevron } from '../icons';
import styles from './Select.module.css';

export const VERTICAL_PADDING_VALUE = 8;

export interface IKeydownEvent {
  key: string;
  preventDefault: () => void;
}

export type UseKeyboardNavigation = {
  highlightedItem: SelectItemType | null;
  items: SelectItemType[];
  onChange: (id: string) => void;
  refHighlightedItem: React.RefObject<HTMLDivElement>;
  refSelectList: React.RefObject<HTMLDivElement>;
  setCurrentSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  setHighlightedItem: React.Dispatch<React.SetStateAction<SelectItemType | null>>;
  setOpenState: (value: React.SetStateAction<boolean>) => void;
  setSearchValue: (value: React.SetStateAction<string>) => void;
};

export type UseToggleDropdown = {
  currentSelectedItem: SelectItemType | null;
  openState: boolean;
  setHighlightedItem: React.Dispatch<React.SetStateAction<SelectItemType | null>>;
  setOpenState: (value: React.SetStateAction<boolean>) => void;
};

export type UseSelectValue = {
  onChange: (id: string) => void;
  setCurrentSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  setOpenState: (value: React.SetStateAction<boolean>) => void;
  setSearchValue: (value: React.SetStateAction<string>) => void;
};

export type UseSelectLayoutEffect = {
  filteredItems: SelectItemType[];
  highlightedItem: SelectItemType | null;
  openState: boolean;
  refHighlightedItem: React.RefObject<HTMLDivElement>;
  refSearchInput: React.RefObject<HTMLInputElement>;
  refSelectList: React.RefObject<HTMLDivElement>;
  search: boolean;
  setHighlightedItem: (item: SelectItemType | null) => void;
};

export const hooks = {
  useCurrentSelectEffect(
    defaultSelectedItemId: string,
    setCurrentSelectedId: (val: string) => void,
  ) {
    React.useEffect(() => {
      setCurrentSelectedId(defaultSelectedItemId);
    }, [defaultSelectedItemId, setCurrentSelectedId]);

    return { defaultSelectedItemId, setCurrentSelectedId };
  },
  useSelectLayoutEffect({
    search,
    openState,
    refSearchInput,
    highlightedItem,
    filteredItems,
    refSelectList,
    refHighlightedItem,
    setHighlightedItem,
  }: UseSelectLayoutEffect) {
    React.useLayoutEffect(() => {
      if (!search && openState) {
        refSearchInput.current?.focus();
      }

      if (openState) {
        if (highlightedItem && filteredItems.length) {
          const existHighlatedItemInList = filteredItems.some(
            (item) => item.id === highlightedItem.id,
          );

          if (!existHighlatedItemInList) {
            setHighlightedItem(filteredItems[0]);
          }
        }

        if (refSelectList.current && refHighlightedItem.current) {
          const highlightedItemOffsetTop = refHighlightedItem.current.offsetTop;
          const highlightedItemHeight = refHighlightedItem.current.clientHeight;
          const highlightedItemOffsetHeight = refHighlightedItem.current.offsetHeight;

          if (highlightedItemOffsetHeight <= highlightedItemHeight) {
            refSelectList.current.scrollTop =
                highlightedItemOffsetTop - highlightedItemHeight - VERTICAL_PADDING_VALUE;
          }
        }
      }
    }, [
      filteredItems,
      highlightedItem,
      openState,
      refHighlightedItem,
      refSearchInput,
      refSelectList,
      search,
      setHighlightedItem,
    ]);

    return {
      selectListScrollTop: refSelectList?.current?.scrollTop,
    };
  },
};

export type SelectItemType = {
  icon?: JSX.Element;
  id: string;
  isDefault?: boolean;
  label: string;
};

type ExtendedProps = {
  customClasses?: {
    chevron?: string;
    select?: string;
    selectHeader?: string;
    selectHeaderValue?: string;
    selectInput?: string;
    selectList?: string;
  };
  defaultSelectedItemId?: string;
  disabled?: boolean;
  itemClasses?: {
    selectItem?: string;
  };
  search?: boolean;
};

type BaseProps = {
  errorClassName?: string;
  errorMessage?: string | JSX.Element | null;
  errorState?: boolean;
  items: SelectItemType[];
  label?: string | JSX.Element;
  labelClassName?: string;
  labelSize?: 'small' | 'default';
  onChange: (id: string) => void;
  placeholder?: string;
};

export type Props = BaseProps & ExtendedProps;

export const Select = React.memo(
  React.forwardRef((
    props: Props,
    ref: React.RefObject<HTMLDivElement>,
  ) => {
    const {
      items,
      defaultSelectedItemId = items.length > 0 ? items[0].id : '',
      disabled = false,
      search = false,
      onChange,
      customClasses,
      itemClasses,
      placeholder = '',
      label,
      labelSize,
      labelClassName,
      errorMessage,
      errorState,
      errorClassName,
    } = props;

    const {
      searchValue,
      setSearchValue,
      openState,
      setOpenState,
      currentSelectedId,
      setCurrentSelectedId,
      highlightedItem,
      setHighlightedItem,
    } = selectHooks.useStates(defaultSelectedItemId);

    const {
      refSearchInput,
      refHighlightedItem,
      refSelectList,
    } = selectHooks.useRefs();

    hooks.useCurrentSelectEffect(defaultSelectedItemId, setCurrentSelectedId);

    const selectedItem = React.useMemo(() => items.find((item: SelectItemType) => {
      return item.id === currentSelectedId;
    }), [currentSelectedId, items]);

    const searchInputValue = selectedItem ? selectedItem.label : placeholder;

    const { filteredItems } = selectHooks.useFilteredItems(items, searchValue);
    domHooks.useClickOutside(ref, selectHooks.useClickOutside(setOpenState, setSearchValue), true);

    const currentSelectedItem = React.useMemo(() => items
      .find((item: SelectItemType) =>
        item.id === currentSelectedId) ?? null, [currentSelectedId, items]);

    const handleToogleDropdown = selectHooks.useToogleDropdown({
      openState,
      setOpenState,
      setHighlightedItem,
      currentSelectedItem,
    });
    const handleSearchInputChange = selectHooks.useSearchInputChange(setSearchValue);

    const handleSelectValue = selectHooks.useSelectValue({
      setOpenState,
      setCurrentSelectedId,
      setSearchValue,
      onChange,
    });

    const { onKeyDown } = selectHooks.useKeyboardNavigation({
      items: filteredItems,
      setOpenState,
      setCurrentSelectedId,
      setSearchValue,
      onChange,
      highlightedItem,
      setHighlightedItem,
      refHighlightedItem,
      refSelectList,
    });

    hooks.useSelectLayoutEffect({
      search,
      openState,
      refSearchInput,
      highlightedItem,
      filteredItems,
      refSelectList,
      refHighlightedItem,
      setHighlightedItem,
    });

    const { isClient } = hydrationHooks.useIsClient();

    return (
      <>
        {label ? (
          <Typography
            component='label'
            variant={labelSize === 'small' ? 'bodySubText2' : 'body2'}
            size={labelSize === 'small' ? 'l' : undefined}
            className={classNames(styles.selectLabel, labelClassName)}
          >
            {label}
          </Typography>
        ) : null}
        <div
          className={classNames(styles.select, customClasses?.select, {
            [styles.disabled]: disabled || !isClient,
          })}
          ref={ref}
        >
          <div className={classNames(styles.selectHeader, customClasses?.selectHeader, {
            [styles.noWrap]: CONFIG.isHyphenation,
          })}>
            {search ? (
              !openState ? (
                <Typography
                  className={classNames(
                    styles.selectHeaderValue,
                    customClasses?.selectHeaderValue,
                  )}
                  onClick={handleToogleDropdown}
                  variant='body1'
                  component='label'
                >
                  <SelectHeaderValue
                    item={selectedItem} placeholder={placeholder} />
                </Typography>
              ) : (
                <input
                  type='text'
                  className={classNames(styles.selectInput, customClasses?.selectInput)}
                  defaultValue={searchValue}
                  placeholder={searchInputValue}
                  autoFocus={true}
                  onChange={handleSearchInputChange}
                  onKeyDown={onKeyDown}
                />
              )
            ) : (
              <>
                <Typography
                  className={classNames(
                    styles.selectHeaderValue,
                    customClasses?.selectHeaderValue,
                  )}
                  onClick={handleToogleDropdown}
                  variant='body1'
                  component='label'
                >
                  <SelectHeaderValue
                    item={selectedItem} placeholder={placeholder} />
                </Typography>
                <input
                  type='text'
                  className={classNames(
                    styles.selectInput,
                    styles.noSearchableInputDefaultState,
                    customClasses?.selectInput,
                    {
                      [styles.noSearchableInputOpenedListState]: openState,
                    },
                  )}
                  defaultValue={searchValue}
                  placeholder={searchInputValue}
                  onChange={handleSearchInputChange}
                  onKeyDown={onKeyDown}
                  readOnly={!search}
                  ref={refSearchInput}
                />
              </>
            )}
            <div>
              <Chevron
                className={classNames(styles.chevron, customClasses?.chevron, {
                  [styles.chevronUp]: openState,
                })}
                width={24}
                height={24}
              />
            </div>
          </div>

          {filteredItems.length ? (
            <div
              className={classNames(styles.selectList, customClasses?.selectList, {
                [styles.selectListOpened]: openState,
              })}
              ref={refSelectList}
            >
              {filteredItems.map((item: SelectItemType) => (
                <SelectItem
                  key={item.id}
                  item={item}
                  currentSelectedId={currentSelectedId}
                  highlighted={item.id === highlightedItem?.id}
                  handleSelectValue={handleSelectValue}
                  ref={item.id === highlightedItem?.id ? refHighlightedItem : null}
                  classes={itemClasses}
                />
              ))}
            </div>
          ) : null}
          { errorState && errorMessage && (
            <CustomErrorMessage
              error={errorMessage}
              className={errorClassName}
              errorColor={CustomErrorMessageColor.Error}
            />
          )}
        </div>
      </>
    );
  }),
);

Select.displayName = 'Select';
